import { useCallback, useEffect, useRef, useState } from 'react';
import { logger } from '../utils/log';
import type { FaceLandmarks, FaceLandmark, FaceBox } from './types';

const log = logger('face');

const DEFAULT_MODEL_URL = '/mediapipe/face_landmarker.task';
const DEFAULT_WASM_URL = '/mediapipe/wasm';

export type FaceTrackerState = 'loading' | 'ready' | 'unavailable';

export interface UseFaceTrackerOptions {
  video: HTMLVideoElement | null;
  enabled: boolean;
  targetHz?: number;
  modelUrl?: string;
  wasmBaseUrl?: string;
}

export interface FaceTrackerApi {
  state: FaceTrackerState;
  /**
   * Imperative getter for the most recent landmark frame. Consumers should
   * read this each render frame (e.g. inside the GPU engine's RAF callback)
   * rather than relying on state-triggered re-renders — landmarks update at
   * ~15 Hz and would otherwise force a full React tree rebuild on every
   * detection.
   */
  getLatest: () => FaceLandmarks | null;
}

interface FaceLandmarkerLike {
  detectForVideo(
    video: HTMLVideoElement,
    timestamp: number,
  ): {
    faceLandmarks?: { x: number; y: number; z: number }[][];
    faceBlendshapes?: { categories: { categoryName: string; score: number }[] }[];
  };
  close?(): void;
}

function boundingBoxFor(face: { x: number; y: number }[]): FaceBox {
  let minX = 1;
  let minY = 1;
  let maxX = 0;
  let maxY = 0;
  for (const p of face) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }
  const width = Math.max(0, maxX - minX);
  const height = Math.max(0, maxY - minY);
  return {
    x: minX,
    y: minY,
    width,
    height,
    centerX: minX + width / 2,
    centerY: minY + height / 2,
  };
}

/**
 * Face landmark detection on the MAIN THREAD.
 *
 * We previously ran this in a module worker, but MediaPipe's Emscripten wasm
 * loader uses `importScripts()` to set a global `ModuleFactory`, which
 * doesn't exist in module workers. The result was a hard `"ModuleFactory not
 * set"` error on every browser, with both GPU and CPU delegates.
 *
 * On the main thread, the loader can use a regular script tag (or
 * fetch+eval into the global scope) and everything wires up. Inference cost
 * at 15 Hz with the float16 model is ~5–10ms per frame, which is well
 * within budget alongside our 30fps WebGL render loop.
 */
export function useFaceTracker(opts: UseFaceTrackerOptions): FaceTrackerApi {
  const { video, enabled } = opts;
  const targetHz = opts.targetHz ?? 15;
  const [state, setState] = useState<FaceTrackerState>(enabled ? 'loading' : 'unavailable');
  const latestRef = useRef<FaceLandmarks | null>(null);
  const landmarkerRef = useRef<FaceLandmarkerLike | null>(null);
  const stoppedRef = useRef(false);
  const getLatest = useCallback(() => latestRef.current, []);

  // Initialize the landmarker on mount.
  useEffect(() => {
    if (!enabled) {
      setState('unavailable');
      return;
    }
    stoppedRef.current = false;
    let cancelled = false;

    (async () => {
      try {
        const modelUrl = opts.modelUrl ?? DEFAULT_MODEL_URL;
        const wasmBaseUrl = opts.wasmBaseUrl ?? DEFAULT_WASM_URL;
        log.info('init starting', { modelUrl, wasmBaseUrl });
        const t0 = performance.now();
        const mod = await import('@mediapipe/tasks-vision');
        log.info('tasks-vision module loaded', `${(performance.now() - t0).toFixed(0)}ms`);
        const fileset = await mod.FilesetResolver.forVisionTasks(wasmBaseUrl);
        log.info('fileset resolved', `${(performance.now() - t0).toFixed(0)}ms`);

        const tryCreate = async (
          delegate: 'GPU' | 'CPU',
        ): Promise<FaceLandmarkerLike> => {
          log.info(`attempting createFromOptions(delegate=${delegate})`);
          return (await mod.FaceLandmarker.createFromOptions(fileset, {
            baseOptions: { modelAssetPath: modelUrl, delegate },
            runningMode: 'VIDEO',
            // Group selfies — track up to 4 faces so smile-shutter can wait
            // for everyone, and overlays still anchor when multiple guests
            // share the frame.
            numFaces: 4,
            // Blendshapes give us mouthSmileLeft/Right for smile detection.
            // Cost is small at our 15 Hz cadence.
            outputFaceBlendshapes: true,
            outputFacialTransformationMatrixes: false,
          })) as FaceLandmarkerLike;
        };

        let landmarker: FaceLandmarkerLike;
        try {
          landmarker = await tryCreate('GPU');
          log.info('GPU delegate ready', `${(performance.now() - t0).toFixed(0)}ms`);
        } catch (gpuErr) {
          log.warn(
            'GPU delegate failed, falling back to CPU:',
            gpuErr instanceof Error ? gpuErr.message : gpuErr,
          );
          landmarker = await tryCreate('CPU');
          log.info('CPU delegate ready', `${(performance.now() - t0).toFixed(0)}ms`);
        }

        if (cancelled) {
          landmarker.close?.();
          return;
        }
        landmarkerRef.current = landmarker;
        setState('ready');
      } catch (err) {
        log.error('init failed', err);
        if (!cancelled) setState('unavailable');
      }
    })();

    return () => {
      cancelled = true;
      stoppedRef.current = true;
      try {
        landmarkerRef.current?.close?.();
      } catch {
        /* ignore */
      }
      landmarkerRef.current = null;
    };
  }, [enabled, opts.modelUrl, opts.wasmBaseUrl]);

  // Detection loop. Throttled to `targetHz` and gated on landmarker readiness.
  useEffect(() => {
    if (!enabled || !video || state !== 'ready') return;
    const interval = 1000 / Math.max(1, targetHz);
    let last = 0;
    let rafHandle = 0;

    const tick = (now: number) => {
      if (stoppedRef.current) return;
      rafHandle = requestAnimationFrame(tick);
      if (now - last < interval) return;
      if (!video || video.readyState < 2 || video.videoWidth === 0) return;
      const lm = landmarkerRef.current;
      if (!lm) return;
      last = now;
      try {
        const res = lm.detectForVideo(video, now);
        const faces: FaceLandmark[][] = (res.faceLandmarks ?? []) as FaceLandmark[][];
        const boxes: FaceBox[] = faces.map((f) => boundingBoxFor(f));
        const smiles: number[] = (res.faceBlendshapes ?? []).map((bs) => {
          let l = 0;
          let r = 0;
          for (const c of bs.categories) {
            if (c.categoryName === 'mouthSmileLeft') l = c.score;
            else if (c.categoryName === 'mouthSmileRight') r = c.score;
          }
          return (l + r) / 2;
        });
        // Stash in a ref — the engine polls this each render frame. No React
        // state updates here keeps us from re-rendering at 15 Hz.
        latestRef.current = { faces, boxes, smiles, timestamp: now };
      } catch (err) {
        log.warn('detect error', err);
      }
    };
    rafHandle = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(rafHandle);
    };
  }, [enabled, video, state, targetHz]);

  return { state, getLatest };
}
